import React, { useMemo } from 'react';
import {
  GetVoiceAndToneCurrentAndTargetVectorsResponse,
  ToneType,
  VoiceType
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import { ProductContent } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/ProductLevelContentAdapter';
import { ChartDataAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import BeaconProChart from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { CheckmarkSolidCircle } from 'src/components/SvgIcons/CheckmarkSolidCircle';
import { hexToRgba } from '@stackline/ui';
import LegendWithMetric from 'src/components/BeaconV2Components/Legend/LegendWithMetric';
import HighchartsReact from 'highcharts-react-official';
import Highcharts, { SeriesOptionsType } from 'src/components/Highcharts';
import ReactDOMServer from 'react-dom/server';
import { useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { DefaultMenuButton } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import ImportedSnackbarCancelUntyped from 'src/components/SvgIcons/snackbarCancel.svg';
import colors from 'src/utils/colors';

interface SvgImportObject {
  id: string;
  viewBox: string;
  content: string;
  node: object; // Or a more specific type if known
}

// We have to do this because the imported svg is not typed
const ImportedSnackbarCancel = ImportedSnackbarCancelUntyped as unknown as SvgImportObject;

// Helper function to extract inner content from symbol string
const getInnerSvgContent = (svgSymbolString: string): string => {
  // Use [^] instead of /s for broader compatibility
  const match = svgSymbolString.match(/<symbol[^>]*>([^]*)<\/symbol>/);
  return match ? match[1] : '';
};

// New component for rendering inline SVGs from sprite objects
interface InlineSvgIconProps {
  svgImport: SvgImportObject;
  color: string;
  style?: React.CSSProperties;
}

const InlineSvgIcon = ({ svgImport, color, style }: InlineSvgIconProps) => {
  // Get the raw inner SVG content
  let innerContent = getInnerSvgContent(svgImport.content);

  if (svgImport.id === 'snackbarCancel') {
    innerContent = innerContent.replace(
      /<g data-name="↳color" transform="translate\(3 3\)" fill="[^\"]*" stroke="[^\"]*" stroke-width="\.75">/,
      '<g data-name="↳color" transform="translate(3 3)" stroke-width=".75">'
    );
  }

  return (
    <svg viewBox={svgImport.viewBox} fill={color} style={style} dangerouslySetInnerHTML={{ __html: innerContent }} />
  );
};

const Tooltip = ({ currentValue, targetValue }: { currentValue: number; targetValue: number }) => {
  return (
    <div
      style={{
        display: 'flex',
        background: '#FFFFFF',
        flexDirection: 'column',
        minHeight: '72px',
        minWidth: '180px',
        padding: '12px',
        borderRadius: '4px',
        border: `1px solid ${colors.graysSecondary}`,
        gap: '8px'
      }}
    >
      <div style={{ height: '24px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <p
          style={{
            margin: 0,
            color: colors.grey,
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: 'Roboto',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '0.96px',
            textTransform: 'uppercase'
          }}
        >
          MATCH SCORE
        </p>
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '4px', height: '40px', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '16px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '24px', backgroundColor: colors.darkBlue }}></div>
            <p
              style={{
                color: colors.grey,
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: 'Roboto',
                fontSize: '10px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '16px',
                letterSpacing: '0.96px',
                textTransform: 'uppercase',
                margin: 0
              }}
            >
              CURRENT
            </p>
          </div>
          <p
            style={{
              color: colors.darkBlue,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.08px',
              margin: 0,
              marginLeft: '16px'
            }}
          >
            {currentValue}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '4px', height: '40px', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '16px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '24px', backgroundColor: '#71C184' }}></div>
            <p
              style={{
                color: colors.grey,
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: 'Roboto',
                fontSize: '10px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '16px',
                letterSpacing: '0.96px',
                textTransform: 'uppercase',
                margin: 0
              }}
            >
              TARGET
            </p>
          </div>
          <p
            style={{
              color: colors.darkBlue,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.08px',
              margin: 0,
              marginLeft: '16px'
            }}
          >
            {targetValue}
          </p>
        </div>
      </div>
    </div>
  );
};

const VoiceAndToneProductColumnChart = ({
  categories,
  series
}: {
  categories: string[];
  series: (SeriesOptionsType & { borderRadiusTopLeft?: string; borderRadiusTopRight?: string })[];
}) => {
  const options = {
    chart: {
      type: 'column',
      width: 488,
      height: 250,
      spacing: [10, 10, 15, 10]
    },
    title: {
      text: ''
    },
    xAxis: {
      tickLength: 0,
      lineColor: 'transparent',

      categories,
      labels: {
        y: 27,
        style: {
          color: colors.darkBlue,
          fontFamily: 'Roboto',
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '18px',
          letterSpacing: '0.08px',
          textAlign: 'center',
          transform: 'translateY(0.5px)'
        }
      }
    },
    yAxis: {
      title: {
        text: ''
      },
      visible: false,
      gridLineWidth: 0,
      min: 0,
      max: 10
    },
    legend: {
      enabled: false
    },
    plotOptions: {
      series: {
        states: {
          hover: {
            enabled: false
          },
          inactive: {
            enabled: false
          }
        }
      }
    },
    series,
    credits: {
      enabled: false
    },
    tooltip: {
      enabled: true,
      shared: false,
      shadow: false,
      hideDelay: 0,
      outside: true,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      useHTML: true,
      formatter() {
        const index = this?.point?.index;
        const allSeries = this?.series?.chart?.series;
        const [currentSeries, targetSeries] = allSeries;
        const currentData = currentSeries?.data;
        const targetData = targetSeries?.data;
        const currentDatum = currentData[index];
        const targetDatum = targetData[index];

        return ReactDOMServer.renderToStaticMarkup(
          <Tooltip currentValue={currentDatum?.y} targetValue={targetDatum.y} />
        );
      }
    }
  };
  return (
    <div className="w-[488px]">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

const MatchBadge = ({ isMatch = false }: { isMatch?: boolean }) => {
  const color = isMatch ? colors.darkGreen : colors.errorRed;
  const backgroundColor = isMatch ? hexToRgba(colors.brandClubGreen, 0.2) : hexToRgba(colors.errorRed, 0.1);

  const icon = isMatch ? (
    <CheckmarkSolidCircle color={color} style={{ width: '17px' }} />
  ) : (
    // Use the new component, so we can save the Chart Image with svg icons
    <InlineSvgIcon svgImport={ImportedSnackbarCancel} color={color} style={{ width: '17px' }} />
  );

  return (
    <Flex
      sx={{
        padding: '4px 8px 4px 4px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        borderRadius: '24px',
        backgroundColor,
        height: '26px'
      }}
      alignItems="center"
    >
      {icon}
      <Text sx={{ color }} variant="body3" fontWeight="500">
        {isMatch ? 'Match' : 'Mismatch'}
      </Text>
    </Flex>
  );
};

const ColumnChartSubheader = ({
  metricLabel,
  currentDominant,
  targetDominant,
  formattedScore
}: {
  metricLabel: 'Tone Match Score' | 'Voice Match Score';
  currentDominant: VoiceType | ToneType;
  targetDominant: VoiceType | ToneType;
  formattedScore: string;
}) => {
  return (
    <Flex gap={16}>
      <Flex gap={4}>
        <Text fontWeight={500} variant="body2">
          Dominant:{' '}
        </Text>
        <Text variant="body2">{currentDominant}</Text>
      </Flex>
      <Flex gap={4}>
        <Text fontWeight={500} variant="body2">
          Desired:{' '}
        </Text>
        <Text variant="body2">{targetDominant}</Text>
      </Flex>
      <Flex gap={4}>
        <Text fontWeight={500} variant="body2">
          {metricLabel}:{' '}
        </Text>
        <Text variant="body2">{formattedScore}</Text>
      </Flex>
    </Flex>
  );
};

const ColumnChartHeader = ({
  title,
  isMatch,
  currentDominant,
  targetDominant,
  formattedScore
}: {
  title: string;
  isMatch: boolean;
  currentDominant: VoiceType | ToneType;
  targetDominant: VoiceType | ToneType;
  formattedScore: string;
}) => {
  return (
    <Flex flexDirection="column" gap={16}>
      <Flex height={28} gap={8} alignItems="center">
        <Text variant="h4" fontWeight="700">
          {title}
        </Text>
        <MatchBadge isMatch={isMatch} />
      </Flex>
      <ColumnChartSubheader
        metricLabel={title === 'Voice' ? 'Voice Match Score' : 'Tone Match Score'}
        currentDominant={currentDominant}
        targetDominant={targetDominant}
        formattedScore={formattedScore}
      />
      <Flex gap="lg">
        <LegendWithMetric color="primary" displayName="Current" />
        <LegendWithMetric color="success" displayName="Target" />
      </Flex>
    </Flex>
  );
};

const ColumnChartContainer = ({ children }: { children: any }) => {
  return (
    <Flex width={488} height="382px" gap={32} flexDirection="column">
      {children}
    </Flex>
  );
};

const VoiceColumnChartContainer = ({ chartData }: { chartData: GetVoiceAndToneCurrentAndTargetVectorsResponse }) => {
  const formatMetric = useMetricFormatter();
  const formatPercent = useMemo(
    () => (value: number) => formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 1 }),
    [formatMetric]
  );

  return (
    <ColumnChartContainer>
      <ColumnChartHeader
        title="Voice"
        isMatch={chartData.currentDominantVoice === chartData.targetDominantVoice}
        currentDominant={chartData.currentDominantVoice}
        targetDominant={chartData.targetDominantVoice}
        formattedScore={formatPercent(chartData.voiceMatchScore)}
      />
      <VoiceAndToneProductColumnChart
        series={[
          {
            name: 'Values',
            type: 'column',
            color: colors.darkBlue,
            borderRadiusTopLeft: '5%',
            borderRadiusTopRight: '5%',
            data: chartData.currentVoiceVector,
            pointWidth: 24
          },
          {
            name: 'Targets',
            type: 'spline',
            lineWidth: 0,
            color: colors.brandClubGreen,
            data: chartData.targetVoiceVector,
            marker: {
              radius: 6
            }
          }
        ]}
        categories={['Informative', 'Persuasive', 'Sustainable', 'Healthy', 'Playful', 'Supportive']}
      />
    </ColumnChartContainer>
  );
};

const ToneColumnChartContainer = ({ chartData }: { chartData: GetVoiceAndToneCurrentAndTargetVectorsResponse }) => {
  const formatMetric = useMetricFormatter();
  const formatPercent = useMemo(
    () => (value: number) => formatMetric(value, METRICTYPE.PERCENT, { decimalPlaces: 1 }),
    [formatMetric]
  );
  return (
    <ColumnChartContainer>
      <ColumnChartHeader
        title="Tone"
        isMatch={chartData.currentDominantTone === chartData.targetDominantTone}
        currentDominant={chartData.currentDominantTone}
        targetDominant={chartData.targetDominantTone}
        formattedScore={formatPercent(chartData.toneMatchScore)}
      />
      <VoiceAndToneProductColumnChart
        series={[
          {
            name: 'Values',
            type: 'column',
            color: colors.darkBlue,
            borderRadiusTopLeft: '5%',
            borderRadiusTopRight: '5%',
            data: chartData?.currentToneVector,
            pointWidth: 24
          },
          {
            name: 'Targets',
            type: 'scatter',
            color: colors.brandClubGreen,
            data: chartData?.targetToneVector,
            marker: {
              radius: 6
            }
          }
        ]}
        categories={['Friendly', 'Luxurious', 'Humorous', 'Encouraging', 'Enthusiastic', 'Confident']}
      />
    </ColumnChartContainer>
  );
};

interface ProductLevelVoiceAndToneAnalysisChartProps {
  title: string;
  queryFn: () => Promise<ChartDataAdapter<ProductContent>>;
  renderSubtitle: (data: GetVoiceAndToneCurrentAndTargetVectorsResponse) => string | React.ReactNode;
}

export const ProductLevelVoiceAndToneAnalysisChart = (props: ProductLevelVoiceAndToneAnalysisChartProps) => {
  const { title, queryFn, renderSubtitle } = props;

  return (
    <BeaconProChart
      name={`voice-and-tone-analysis-chart-${title.toLowerCase().replace(/\s+/g, '-')}`}
      queryFn={queryFn}
    >
      <BeaconProChart.Header menuButton={<DefaultMenuButton excludeActions={[]} />}>
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(data: ChartDataAdapter<GetVoiceAndToneCurrentAndTargetVectorsResponse>) => {
          return renderSubtitle(data.transform());
        }}
      />

      <BeaconProChart.Visualization
        render={(data: ChartDataAdapter<GetVoiceAndToneCurrentAndTargetVectorsResponse>, isLoading: boolean) => {
          if (isLoading) {
            // TODO: add loading skeleton
            return null;
          }

          const chartData = data.transform();

          return (
            <Flex marginTop="32px" gap="md" justifyContent="space-between" alignItems="flex-start">
              <VoiceColumnChartContainer chartData={chartData} />
              <ToneColumnChartContainer chartData={chartData} />
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
